/**
 * 自家 BFF 路由的前端封裝（不含任何共享密鑰與簽名邏輯）。
 *
 * 為什麼要經 BFF：後端交付文檔第 1、5 節明確要求「瀏覽器不能持有共享密鑰，必須經由 BFF /
 * 網關代簽名」。SPA 的構建產物、瀏覽器記憶體、DevTools 與 Network 都可能洩漏 X-Api-Key，
 * 即使改用 Web Crypto 在瀏覽器計算簽名也保護不了密鑰，所以簽名一律在服務端完成（見 bff/）。
 */

import { calibrateFromPayload } from '@/utils/server-time'

/** BFF 路由預設值；可用執行時配置 MAP_KEY_BFF_PATH 覆蓋（例如走別的網域或網關） */
export const DEFAULT_CLIENT_SIDE_KEY_ROUTE = '/bff/maps/client-side-key';

/** 對外只顯示通用文案，不暴露上游認證錯誤、狀態碼或請求頭（文檔第 5 節） */
export const GENERIC_MAP_ERROR_MESSAGE = '地圖服務暫時無法使用，請稍後再試';

/**
 * 取得 Google Maps 前端 key
 * @param {String} [path] BFF 路由，預設 DEFAULT_CLIENT_SIDE_KEY_ROUTE
 * @returns {Promise<{code:(Number|undefined), message:(String|undefined), data:{clientSideKey:String}, serverTime:(Number|undefined)}>}
 *          回應格式與直接呼叫後端介面一致（含頂層 serverTime），呼叫方不需要區分走的是哪條鏈路
 */
export function fetchClientSideKey(path) {
  const route = path || DEFAULT_CLIENT_SIDE_KEY_ROUTE;

  return fetch(route, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then((response) => response.json()
    .catch(() => ({}))
    .then((payload) => {
      // serverTime 與 code / data 同級，位於回應體頂層（這裡的 payload 就是回應體，即 payload.serverTime）：
      // 成功或失敗都先拿它校準本機時鐘，
      // 之後簽名用的 X-Timestamp 就取伺服器時間（後端要求，不要前端自己生成）
      calibrateFromPayload(payload);

      if (!response.ok) {
        // 不記錄、不轉發上游錯誤細節；只給通用文案
        throw new Error(GENERIC_MAP_ERROR_MESSAGE);
      }
      const clientSideKey = payload && payload.data && payload.data.clientSideKey;
      if (!clientSideKey) {
        throw new Error(GENERIC_MAP_ERROR_MESSAGE);
      }
      const result = {
        code: payload.code,
        message: payload.message,
        data: { clientSideKey },
      };
      // 原樣透傳 serverTime，維持與直連後端相同的信封格式
      if (payload.serverTime !== undefined) {
        result.serverTime = payload.serverTime;
      }
      return result;
    }));
}
